// Read/write for the `system/infra-params` Metadata row (Task 12): the
// install's real bicep suffix + params, stamped by deploy and by a full
// converge so the updater job can run infra converge without guessing the
// suffix — see infra-params.ts's secret audit for why the plain row is safe.
import { TableClient } from "@azure/data-tables";
import type { TokenCredential } from "@azure/identity";
import { type InfraParams, entityToInfraParams, infraParamsToEntity, INFRA_PARAMS_PK, INFRA_PARAMS_RK } from "@m8t-stack/platform-release";
import { runAz } from "./az.js";
import { discoverStampStorage } from "./platform-storage-discovery.js";

/** Resolve a `TableClient` for the `Metadata` table that holds `system/infra-params` — same
 *  table + discovery as the stamp/apply-request rows (see platform-stamp.ts / apply-request-store.ts). */
export async function openInfraParamsTable(opts: {
  credential: TokenCredential;
  subscriptionId: string;
  resourceGroup: string;
}): Promise<TableClient> {
  const { tableEndpoint } = await discoverStampStorage(opts);
  return new TableClient(tableEndpoint, "Metadata", opts.credential);
}

export async function readInfraParams(client: TableClient): Promise<InfraParams | null> {
  try {
    return entityToInfraParams(await client.getEntity<{ value: string }>(INFRA_PARAMS_PK, INFRA_PARAMS_RK));
  } catch {
    return null;
  }
}

export async function writeInfraParams(client: TableClient, p: InfraParams): Promise<void> {
  await client.upsertEntity(infraParamsToEntity(p), "Replace");
}

/**
 * Fallback ONLY when the `system/infra-params` row is absent: derive the
 * suffix from the gateway container app name (`m8t-gateway-<suffix>`) and
 * VERIFY it by confirming the matching Container Apps Environment
 * (`m8t-cae-<suffix>`) also exists. Refuses to guess on ANY ambiguity — zero
 * or more than one gateway match, or a suffix whose CAE doesn't verify —
 * returning null rather than risk provisioning a duplicate stack.
 */
export async function deriveSuffixVerified(opts: {
  credential?: TokenCredential;
  subscriptionId: string;
  resourceGroup: string;
}): Promise<string | null> {
  const list = JSON.parse(
    await runAz([
      "containerapp",
      "list",
      "-g",
      opts.resourceGroup,
      "--subscription",
      opts.subscriptionId,
      "--query",
      "[?starts_with(name, 'm8t-gateway-')].name",
      "-o",
      "json",
    ]),
  ) as string[];
  if (list.length !== 1) return null; // 0 or >1 → refuse to guess
  const suffix = list[0].replace(/^m8t-gateway-/, "");
  // Verify the suffix by confirming the matching CAE exists too.
  const cae = JSON.parse(
    await runAz([
      "containerapp",
      "env",
      "list",
      "-g",
      opts.resourceGroup,
      "--subscription",
      opts.subscriptionId,
      "--query",
      `[?name=='m8t-cae-${suffix}'].name`,
      "-o",
      "json",
    ]),
  ) as string[];
  return cae.length === 1 ? suffix : null;
}
